import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { userService } from '../../services/api';

const Profile = () => {
    const { currentUser, updateUserProfile } = useAuth();
    const navigate = useNavigate();

    const [profileData, setProfileData] = useState({
                                                       name: '',
                                                       email: '',
                                                       phone: '',
                                                       username: '',
                                                       notificationPreferences: {
                                                           email: true,
                                                           sms: false
                                                       }
                                                   });

    const [passwordData, setPasswordData] = useState({
                                                         currentPassword: '',
                                                         newPassword: '',
                                                         confirmPassword: ''
                                                     });

    const [loading, setLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('profile');
    const [successMessage, setSuccessMessage] = useState('');
    const [error, setError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');

    useEffect(() => {
        const loadUserProfile = async () => {
            setLoading(true);
            try {
                const response = await userService.getMe();
                const userData = response.data;
                setProfileData({
                                   name: userData.name || '',
                                   email: userData.email || '',
                                   phone: userData.phone || '',
                                   username: userData.username || '',
                                   notificationPreferences: userData.notificationPreferences || {
                                       email: true,
                                       sms: false
                                   }
                               });
            } catch (error) {
                console.error("Error loading user profile:", error);
                setError("Failed to load profile data. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        if (currentUser && currentUser.id) {
            loadUserProfile();
        } else {
            navigate('/login');
        }
    }, [currentUser, navigate]);

    const handleProfileInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.startsWith('notification_')) {
            const prefName = name.replace('notification_', '');
            setProfileData(prev => ({
                ...prev,
                notificationPreferences: {
                    ...prev.notificationPreferences,
                    [prefName]: checked
                }
            }));
        } else {
            setProfileData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handlePasswordInputChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setSaveLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const updatedProfile = {
                name: profileData.name,
                phone: profileData.phone,
                username: profileData.username,
                notificationPreferences: profileData.notificationPreferences
            };

            await userService.updateNotificationPreferences(currentUser.id, profileData.notificationPreferences);
            await userService.updateUser(currentUser.id, updatedProfile);

            // Update user context if needed
            if (typeof updateUserProfile === 'function') {
                updateUserProfile({
                                      ...currentUser,
                                      name: profileData.name,
                                      username: profileData.username
                                  });
            }

            setSuccessMessage('Profile updated successfully!');

            // Hide success message after 3 seconds
            setTimeout(() => {
                setSuccessMessage('');
            }, 3000);
        } catch (error) {
            console.error("Error updating profile:", error);
            setError('Failed to update profile. Please try again.');
        } finally {
            setSaveLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPasswordLoading(true);
        setPasswordError('');
        setPasswordSuccess('');

        // Validate passwords
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordError('New passwords do not match.');
            setPasswordLoading(false);
            return;
        }

        if (passwordData.newPassword.length < 8) {
            setPasswordError('Password must be at least 8 characters long.');
            setPasswordLoading(false);
            return;
        }

        try {
            await userService.changePassword({
                                                 currentPassword: passwordData.currentPassword,
                                                 newPassword: passwordData.newPassword
                                             });

            setPasswordSuccess('Password changed successfully!');
            setPasswordData({
                                currentPassword: '',
                                newPassword: '',
                                confirmPassword: ''
                            });

            // Hide success message after 3 seconds
            setTimeout(() => {
                setPasswordSuccess('');
            }, 3000);
        } catch (error) {
            console.error("Error changing password:", error);
            setPasswordError(error.response?.data?.message || 'Failed to change password. Please check your current password.');
        } finally {
            setPasswordLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">My Profile</h1>

            <div className="bg-white rounded-lg shadow mb-6">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex">
                        <button
                            className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm ${
                                activeTab === 'profile'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                            onClick={() => setActiveTab('profile')}
                        >
                            Profile Information
                        </button>
                        <button
                            className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm ${
                                activeTab === 'password'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                            onClick={() => setActiveTab('password')}
                        >
                            Change Password
                        </button>
                        <button
                            className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm ${
                                activeTab === 'notifications'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                            onClick={() => setActiveTab('notifications')}
                        >
                            Notification Preferences
                        </button>
                    </nav>
                </div>

                <div className="p-6">
                    {activeTab === 'profile' && (
                        <form onSubmit={handleSaveProfile}>
                            {error && (
                                <div className="mb-4 p-4 border border-red-400 bg-red-50 rounded-md text-red-700">
                                    {error}
                                </div>
                            )}

                            {successMessage && (
                                <div className="mb-4 p-4 border border-green-400 bg-green-50 rounded-md text-green-700">
                                    {successMessage}
                                </div>
                            )}

                            <div className="space-y-6">
                                <div className="flex flex-col md:flex-row md:space-x-4">
                                    <div className="w-full md:w-1/2 mb-4 md:mb-0">
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                            Full Name
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={profileData.name}
                                            onChange={handleProfileInputChange}
                                        />
                                    </div>
                                    <div className="w-full md:w-1/2">
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 focus:outline-none"
                                            value={profileData.email}
                                            readOnly
                                            disabled
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            Email cannot be changed. Contact an administrator for assistance.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex flex-col md:flex-row md:space-x-4">
                                    <div className="w-full md:w-1/2 mb-4 md:mb-0">
                                        <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                                            Username
                                        </label>
                                        <input
                                            type="text"
                                            id="username"
                                            name="username"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={profileData.username}
                                            onChange={handleProfileInputChange}
                                        />
                                    </div>
                                    <div className="w-full md:w-1/2">
                                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={profileData.phone}
                                            onChange={handleProfileInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="pt-4 flex justify-end">
                                    <button
                                        type="submit"
                                        className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                            saveLoading ? 'opacity-70 cursor-not-allowed' : ''
                                        }`}
                                        disabled={saveLoading}
                                    >
                                        {saveLoading ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}

                    {activeTab === 'password' && (
                        <form onSubmit={handleChangePassword}>
                            {passwordError && (
                                <div className="mb-4 p-4 border border-red-400 bg-red-50 rounded-md text-red-700">
                                    {passwordError}
                                </div>
                            )}

                            {passwordSuccess && (
                                <div className="mb-4 p-4 border border-green-400 bg-green-50 rounded-md text-green-700">
                                    {passwordSuccess}
                                </div>
                            )}

                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                                        Current Password
                                    </label>
                                    <input
                                        type="password"
                                        id="currentPassword"
                                        name="currentPassword"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={passwordData.currentPassword}
                                        onChange={handlePasswordInputChange}
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                                        New Password
                                    </label>
                                    <input
                                        type="password"
                                        id="newPassword"
                                        name="newPassword"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={passwordData.newPassword}
                                        onChange={handlePasswordInputChange}
                                        required
                                        minLength={8}
                                    />
                                    <p className="text-xs text-gray-500">
                                        Password must be at least 8 characters long.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                        Confirm New Password
                                    </label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={passwordData.confirmPassword}
                                        onChange={handlePasswordInputChange}
                                        required
                                    />
                                </div>

                                <div className="pt-4 flex justify-end">
                                    <button
                                        type="submit"
                                        className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                            passwordLoading ? 'opacity-70 cursor-not-allowed' : ''
                                        }`}
                                        disabled={passwordLoading}
                                    >
                                        {passwordLoading ? 'Changing Password...' : 'Change Password'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}

                    {activeTab === 'notifications' && (
                        <form onSubmit={handleSaveProfile}>
                            {error && (
                                <div className="mb-4 p-4 border border-red-400 bg-red-50 rounded-md text-red-700">
                                    {error}
                                </div>
                            )}

                            {successMessage && (
                                <div className="mb-4 p-4 border border-green-400 bg-green-50 rounded-md text-green-700">
                                    {successMessage}
                                </div>
                            )}

                            <div className="space-y-6">
                                <div className="space-y-4">
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="notification_email"
                                            name="notification_email"
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            checked={profileData.notificationPreferences.email}
                                            onChange={handleProfileInputChange}
                                        />
                                        <label htmlFor="notification_email" className="ml-2 block text-sm text-gray-700">
                                            Email Notifications
                                        </label>
                                    </div>
                                    <p className="text-xs text-gray-500 pl-6">
                                        Receive notifications about orders, inventory, and system updates via email.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="notification_sms"
                                            name="notification_sms"
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            checked={profileData.notificationPreferences.sms}
                                            onChange={handleProfileInputChange}
                                        />
                                        <label htmlFor="notification_sms" className="ml-2 block text-sm text-gray-700">
                                            SMS Notifications
                                        </label>
                                    </div>
                                    <p className="text-xs text-gray-500 pl-6">
                                        Receive important alerts and notifications via text message.
                                    </p>
                                </div>

                                <div className="pt-4 flex justify-end">
                                    <button
                                        type="submit"
                                        className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                            saveLoading ? 'opacity-70 cursor-not-allowed' : ''
                                        }`}
                                        disabled={saveLoading}
                                    >
                                        {saveLoading ? 'Saving...' : 'Save Preferences'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900">Account Information</h2>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h3 className="text-sm font-medium text-gray-500">Account Type</h3>
                            <p className="mt-1 text-sm text-gray-900">
                                {currentUser.roles && currentUser.roles.map(role => role.name).join(', ')}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-gray-500">Account Status</h3>
                            <p className="mt-1 text-sm text-gray-900">
                                {currentUser.is_active ? (
                                    <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                    Active
                  </span>
                                ) : (
                                     <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                    Inactive
                  </span>
                                 )}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-gray-500">Last Login</h3>
                            <p className="mt-1 text-sm text-gray-900">
                                {currentUser.last_login ? new Date(currentUser.last_login).toLocaleString() : 'Never'}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-gray-500">Member Since</h3>
                            <p className="mt-1 text-sm text-gray-900">
                                {currentUser.created_at ? new Date(currentUser.created_at).toLocaleDateString() : 'Unknown'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
